import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.jsx';
import { Textarea } from './ui/textarea.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs.jsx';
import axios from 'axios';
import { toast } from 'sonner';

const AdminRegistraciones = () => {
  const { user, API } = useContext(AuthContext);
  
  // Helper function for payment method names
  const getPaymentMethodName = (metodo_pago) => {
    const paymentMethods = {
      'tarjeta_credito_debito': 'Tarjeta de Crédito/Débito',
      'transferencia_bancaria': 'Transferencia Bancaria',
      'efectivo': 'Efectivo',
      'estudiantes_becado': 'Estudiantes Becado',
      // Legacy support
      'tarjeta_credito': 'Tarjeta de Crédito/Débito',
      'contado': 'Efectivo',
      'transferencia': 'Transferencia Bancaria',
      'stripe': 'Tarjeta de Crédito/Débito'
    };
    return paymentMethods[metodo_pago] || metodo_pago;
  };

  /**
   * Abre Loyverse POS (Android) para reimprimir un recibo desde la impresora térmica
   * pareada por Bluetooth. Copia el número de recibo al portapapeles para que el
   * usuario lo pegue rápido en el buscador de Loyverse.
   */
  const openLoyverseReceipt = async (receiptNumber) => {
    if (!receiptNumber) {
      toast.error('Este registro no tiene recibo Loyverse emitido');
      return;
    }
    // 1) Copy receipt number to clipboard (so user pastes it in Loyverse search)
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(receiptNumber));
      }
    } catch (_) { /* ignore clipboard errors */ }

    const ua = (navigator.userAgent || '').toLowerCase();
    const isAndroid = ua.includes('android');

    if (isAndroid) {
      // Android intent: try opening Loyverse POS app (com.loyverse.sale) — donde
      // está pareada la impresora térmica. Si no está instalada, abre Play Store.
      const intentUrl =
        'intent://#Intent;scheme=loyverse;package=com.loyverse.sale;' +
        'S.browser_fallback_url=' +
        encodeURIComponent('https://play.google.com/store/apps/details?id=com.loyverse.sale') +
        ';end';
      // Use location.href so Android Chrome processes the intent properly
      window.location.href = intentUrl;
      toast.success(
        `Recibo #${receiptNumber} copiado. Abriendo Loyverse POS — pegá el número en Recibos para reimprimir.`,
        { duration: 6000 }
      );
    } else {
      // Desktop/iOS fallback: open Loyverse Back Office in browser
      window.open('https://r.loyverse.com/dashboard/#/receipts', '_blank', 'noopener');
      toast.success(
        `Recibo #${receiptNumber} copiado. Pegá el número en el buscador de Loyverse para reimprimir.`,
        { duration: 6000 }
      );
    }
  };

  const [loading, setLoading] = useState(true);
  const [registracionesPendientes, setRegistracionesPendientes] = useState([]);
  const [todasRegistraciones, setTodasRegistraciones] = useState([]);
  const [estadisticas, setEstadisticas] = useState({});
  
  // Estados para revisión
  const [revisingRegistration, setRevisingRegistration] = useState(null);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    accion: '',
    comentarios: ''
  });

  // Estados para facturación
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [selectedForInvoice, setSelectedForInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    fecha_vencimiento: '',
    descuento_porcentaje: 0,
    nota_adicional: ''
  });

  // Estados para configuración de link de registro
  const [registroConfig, setRegistroConfig] = useState(null);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [configForm, setConfigForm] = useState({
    link_registro: '',
    tipo_formulario: 'wix',
    titulo: 'Formulario de Registro Adicional',
    descripcion: 'Completa este formulario adicional como parte de tu proceso de registro'
  });

  // Estados para eliminación
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [comprobanteModal, setComprobanteModal] = useState({ open: false, url: null, registroId: null, estudianteNombre: null });
  const [deletingRegistration, setDeletingRegistration] = useState(null);
  const [uploadingToMega, setUploadingToMega] = useState(false);
  const [searchParams] = useSearchParams();
  const [highlightedRegistroId, setHighlightedRegistroId] = useState(null);

  useEffect(() => {
    if (user?.rol === 'admin') {
      loadData();
    }
  }, [user]);

  // Deep-link: si la URL incluye ?registro_id=xxx (notificación push de comprobante)
  // hacer scroll al registro y resaltarlo brevemente.
  useEffect(() => {
    if (loading) return;
    const target = searchParams.get('registro_id');
    if (!target) return;
    // Buscar en pendientes primero (típico caso de comprobante subido)
    const all = [...(registracionesPendientes || []), ...(todasRegistraciones || [])];
    const found = all.find((r) => r.id === target);
    if (!found) return;
    setHighlightedRegistroId(target);
    setTimeout(() => {
      const el = document.querySelector(`[data-registro-card="${target}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    // Quitar el highlight luego de 4s
    const t = setTimeout(() => setHighlightedRegistroId(null), 4000);
    return () => clearTimeout(t);
  }, [loading, registracionesPendientes, todasRegistraciones, searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar registraciones pendientes
      const pendientesResponse = await axios.get(`${API}/admin/registraciones/pendientes`);
      setRegistracionesPendientes(pendientesResponse.data.registraciones_pendientes || []);

      // Cargar todas las registraciones
      const todasResponse = await axios.get(`${API}/admin/registraciones`);
      setTodasRegistraciones(todasResponse.data.registraciones || []);

      // Cargar estadísticas
      const estadisticasResponse = await axios.get(`${API}/admin/registraciones/estadisticas`);
      setEstadisticas(estadisticasResponse.data || {});

      // Cargar configuración de link de registro
      await loadRegistroConfig();

    } catch (error) {
      toast.error('Error al cargar datos de registraciones');
      console.error('Error loading registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, currency) => {
    const symbol = currency === 'USD' ? '$' : '$';
    const prefix = currency === 'USD' ? 'USD ' : 'UYU ';
    const safePrice = price || 0;
    return `${prefix}${symbol}${safePrice.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleUploadToMega = async () => {
    if (!comprobanteModal.registroId) {
      toast.error('No se puede identificar el registro');
      return;
    }
    
    setUploadingToMega(true);
    try {
      const response = await axios.post(
        `${API}/admin/registracion/${comprobanteModal.registroId}/comprobante/mega`,
        {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }}
      );
      
      if (response.data.success) {
        toast.success(`✅ Recibo subido a MEGA: ${response.data.file_name}`);
      } else {
        toast.error('Error al subir a MEGA');
      }
    } catch (error) {
      console.error('Error uploading to MEGA:', error);
      toast.error(error.response?.data?.detail || 'Error al subir comprobante a MEGA');
    } finally {
      setUploadingToMega(false);
    }
  };

  const getStatusBadge = (estado) => {
    const statusConfig = {
      'pendiente': { color: 'bg-yellow-100 text-yellow-800', text: 'Pendiente', icon: '⏳' },
      'subido': { color: 'bg-blue-100 text-blue-800', text: 'Comprobante Subido', icon: '📄' },
      'enviado_whatsapp': { color: 'bg-emerald-100 text-emerald-800', text: 'Enviado por WhatsApp', icon: '📱' },
      'aprobado': { color: 'bg-green-100 text-green-800', text: 'Aprobado', icon: '✅' },
      'rechazado': { color: 'bg-red-100 text-red-800', text: 'Rechazado', icon: '❌' },
      'pagado': { color: 'bg-green-100 text-green-800', text: 'Pagado (Stripe)', icon: '💳' }
    };

    const config = statusConfig[estado] || statusConfig['pendiente'];
    return (
      <Badge className={config.color}>
        {config.icon} {config.text}
      </Badge>
    );
  };

  const openReviewDialog = (registration) => {
    setRevisingRegistration(registration);
    // Por defecto, sugerir emitir recibo si el monto es > 0 y no está becado
    const sugerirRecibo =
      Number(registration?.monto_pagado || registration?.monto || 0) > 0 &&
      registration?.metodo_pago !== 'becado' &&
      !registration?.loyverse_receipt_number;
    setReviewForm({ accion: '', comentarios: '', emitirReciboLoyverse: sugerirRecibo });
    setIsReviewDialogOpen(true);
  };

  const handleReview = async () => {
    if (!reviewForm.accion) {
      toast.error('Selecciona una acción (aprobar o rechazar)');
      return;
    }
    
    try {
      // Detectar si es registro del nuevo flujo (Plan B) - tiene etapa_proceso definido
      const esNuevoFlujo = revisingRegistration.etapa_proceso && 
        revisingRegistration.etapa_proceso !== 'completado' &&
        revisingRegistration.etapa_proceso !== '';
      
      console.log('Revisando registro:', revisingRegistration.id, 'Plan B:', esNuevoFlujo, 'etapa:', revisingRegistration.etapa_proceso);
      
      if (esNuevoFlujo) {
        // Plan B: Usar el endpoint del nuevo flujo de registro
        await axios.post(`${API}/proceso-registro/${revisingRegistration.id}/validar`, {
          aprobado: reviewForm.accion === 'aprobar',
          motivo_rechazo: reviewForm.accion === 'rechazar' ? (reviewForm.comentarios || 'Rechazado por el administrador') : ''
        });
      } else {
        // Plan A: Usar el endpoint antiguo
        await axios.post(`${API}/admin/registraciones/${revisingRegistration.id}/revision`, {
          accion: reviewForm.accion,
          comentarios: reviewForm.comentarios || ''
        });
      }

      toast.success(
        reviewForm.accion === 'aprobar' 
          ? 'Registración aprobada exitosamente'
          : 'Registración rechazada'
      );

      // Si se aprobó y el admin pidió emitir recibo en Loyverse, llamamos al endpoint
      if (reviewForm.accion === 'aprobar' && reviewForm.emitirReciboLoyverse) {
        try {
          const r = await axios.post(
            `${API}/loyverse/receipts/from-registration/${revisingRegistration.id}`,
            {}
          );
          const receiptNumber = r?.data?.receipt_number;
          if (r?.data?.already_emitted) {
            toast.info(`Ya existía un recibo Loyverse: #${receiptNumber}`);
          } else {
            toast.success(`🧾 Recibo Loyverse #${receiptNumber} emitido (email automático al alumno)`);
          }
        } catch (loyErr) {
          console.error('Loyverse error:', loyErr.response?.data || loyErr);
          toast.error(
            `Aprobación OK, pero falló el recibo Loyverse: ${
              loyErr.response?.data?.detail || loyErr.message
            }`
          );
        }
      }

      loadData();
      setIsReviewDialogOpen(false);
      setRevisingRegistration(null);
    } catch (error) {
      console.error('Error en revisión:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Error al procesar revisión');
    }
  };

  const openDeleteDialog = (registration) => {
    setDeletingRegistration(registration);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/registraciones/${deletingRegistration.id}`);
      
      toast.success('Registro eliminado exitosamente');
      loadData();
      setIsDeleteDialogOpen(false);
      setDeletingRegistration(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar registro');
    }
  };

  const openInvoiceDialog = (registration) => {
    setSelectedForInvoice(registration);
    setInvoiceForm({
      fecha_vencimiento: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 días
      descuento_porcentaje: 0,
      nota_adicional: ''
    });
    setIsInvoiceDialogOpen(true);
  };

  const handleSendInvoice = async () => {
    try {
      await axios.post(`${API}/admin/registraciones/${selectedForInvoice.id}/enviar-factura`, invoiceForm);
      
      toast.success('Factura enviada exitosamente');
      loadData();
      setIsInvoiceDialogOpen(false);
      setSelectedForInvoice(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar factura');
    }
  };

  const downloadReceipt = async (registrationId) => {
    try {
      const response = await axios.get(`${API}/admin/registraciones/${registrationId}/recibo`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recibo_${registrationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar recibo');
    }
  };

  // Funciones para configuración de link de registro
  const loadRegistroConfig = async () => {
    try {
      const response = await axios.get(`${API}/registro-config`);
      if (response.data) {
        setRegistroConfig(response.data);
        setConfigForm({
          link_registro: response.data.link_registro || '',
          tipo_formulario: response.data.tipo_formulario || 'wix',
          titulo: response.data.titulo || 'Formulario de Registro Adicional',
          descripcion: response.data.descripcion || 'Completa este formulario adicional como parte de tu proceso de registro'
        });
      }
    } catch (error) {
      // Si no hay configuración, no es error, simplemente no existe aún
      console.log('No registration config found yet');
    }
  };

  const openConfigDialog = () => {
    setIsConfigDialogOpen(true);
  };

  const handleSaveConfig = async () => {
    try {
      if (!configForm.link_registro.trim()) {
        toast.error('El link de registro es requerido');
        return;
      }

      // Validate URL format
      try {
        new URL(configForm.link_registro);
      } catch (error) {
        toast.error('Por favor ingrese un link válido');
        return;
      }

      const method = registroConfig ? 'PUT' : 'POST';
      const url = registroConfig 
        ? `${API}/admin/registro-config/${registroConfig.id}`
        : `${API}/admin/registro-config`;

      await axios({
        method,
        url,
        data: configForm
      });

      toast.success('Configuración de registro guardada exitosamente');
      loadRegistroConfig(); // Reload config
      setIsConfigDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    }
  };

  const validateFormLink = (link) => {
    try {
      new URL(link);
      return true;
    } catch {
      return false;
    }
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="space-y-6">
        <Card className="shadow-md border-l-4 border-red-500">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <i className="fas fa-exclamation-triangle text-red-500 text-2xl"></i>
              <div>
                <h3 className="font-semibold text-gray-900">Acceso Denegado</h3>
                <p className="text-gray-600">Solo los administradores pueden gestionar registraciones.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard'}
          className="text-gray-600 hover:text-gray-800"
          data-testid="back-button"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Volver
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Panel de Administración de Registraciones</h1>
          <p className="text-gray-600">Gestiona registraciones, formas de pago, facturación y recibos</p>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Registraciones</p>
                <p className="text-3xl font-bold text-blue-600">{estadisticas.total || 0}</p>
              </div>
              <i className="fas fa-clipboard-list text-blue-600 text-3xl"></i>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pendientes</p>
                <p className="text-3xl font-bold text-yellow-600">{estadisticas.pendientes || 0}</p>
              </div>
              <i className="fas fa-clock text-yellow-600 text-3xl"></i>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Aprobadas</p>
                <p className="text-3xl font-bold text-green-600">{estadisticas.aprobadas || 0}</p>
              </div>
              <i className="fas fa-check-circle text-green-600 text-3xl"></i>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Ingresos Total</p>
                <p className="text-3xl font-bold text-indigo-600">${estadisticas.ingresos_total || 0}</p>
              </div>
              <i className="fas fa-dollar-sign text-indigo-600 text-3xl"></i>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuración de Link de Registro */}
      <Card className="shadow-lg border-0 border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <i className="fas fa-link mr-3 text-blue-600"></i>
              Configuración de Link de Registro
            </div>
            <Button onClick={openConfigDialog} className="bg-blue-600 hover:bg-blue-700">
              <i className="fas fa-cog mr-2"></i>
              {registroConfig ? 'Actualizar' : 'Configurar'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {registroConfig ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-800">{registroConfig.titulo}</h4>
                  <p className="text-sm text-gray-600">{registroConfig.descripcion}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Configurado: {new Date(registroConfig.fecha_creacion).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={registroConfig.activo ? "default" : "secondary"}>
                    {registroConfig.activo ? 'Activo' : 'Inactivo'}
                  </Badge>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(registroConfig.link_registro, '_blank')}
                  >
                    <i className="fas fa-external-link-alt mr-1"></i>
                    Ver
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <i className="fas fa-link text-gray-400 text-2xl mb-2"></i>
              <p className="text-gray-600 mb-3">
                No hay configuración de link de registro establecida.
              </p>
              <p className="text-sm text-gray-500">
                Los estudiantes podrán ver un enlace adicional durante el proceso de registro.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="pendientes" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pendientes">
            Pendientes ({registracionesPendientes.length})
          </TabsTrigger>
          <TabsTrigger value="todas">
            Todas las Registraciones
          </TabsTrigger>
          <TabsTrigger value="facturacion">
            Facturación
          </TabsTrigger>
        </TabsList>

        {/* Registraciones Pendientes */}
        <TabsContent value="pendientes">
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center">
                <i className="fas fa-clock mr-3 text-yellow-600"></i>
                Registraciones Pendientes de Aprobación
              </CardTitle>
            </CardHeader>
            <CardContent>
              {registracionesPendientes.length === 0 ? (
                <div className="text-center py-8">
                  <i className="fas fa-check-circle text-green-600 text-5xl mb-4"></i>
                  <p className="text-gray-600">No hay registraciones pendientes</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {registracionesPendientes.map((registro) => (
                    <Card
                      key={registro.id}
                      data-registro-card={registro.id}
                      className={`border-l-4 border-l-yellow-500 transition-all duration-500 ${
                        highlightedRegistroId === registro.id
                          ? 'ring-4 ring-blue-400 shadow-2xl scale-[1.01] bg-blue-50'
                          : ''
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-semibold text-lg">{registro.estudiante_nombre}</h3>
                              {registro.es_renovacion && (
                                <Badge className="bg-purple-500 text-white">
                                  <i className="fas fa-redo mr-1"></i>
                                  Renovación
                                </Badge>
                              )}
                              {!registro.es_renovacion && (
                                <Badge className="bg-green-500 text-white">
                                  <i className="fas fa-star mr-1"></i>
                                  Nuevo
                                </Badge>
                              )}
                              {getStatusBadge(registro.estado_comprobante)}
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">Semestre:</span>
                                <p className="font-medium">{registro.semestre_nombre}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Monto:</span>
                                <p className="font-medium">{formatPrice(registro.monto, registro.moneda)}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Método:</span>
                                <p className="font-medium">{getPaymentMethodName(registro.metodo_pago)}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Fecha:</span>
                                <p className="font-medium">{formatDate(registro.fecha_registro)}</p>
                              </div>
                            </div>

                            {(registro.comprobante_url || registro.comprobante_pago_url) && (
                              <div className="mt-3">
                                <button
                                  onClick={() => {
                                    const url = registro.comprobante_url || registro.comprobante_pago_url;
                                    if (!url) {
                                      toast.error('No hay comprobante disponible');
                                      return;
                                    }
                                    setComprobanteModal({ 
                                      open: true, 
                                      url: url,
                                      registroId: registro.id,
                                      estudianteNombre: registro.estudiante_nombre
                                    });
                                  }}
                                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center cursor-pointer bg-transparent border-0"
                                >
                                  <i className="fas fa-file-image mr-1"></i>
                                  Ver Comprobante de Pago
                                </button>
                              </div>
                            )}

                            {/* Aviso: recibo enviado por WhatsApp */}
                            {(registro.metodo_envio_comprobante === 'whatsapp' || registro.estado_comprobante === 'enviado_whatsapp') && (
                              <div className="mt-3 bg-green-50 border-2 border-green-300 rounded-lg p-3 flex items-start gap-3" data-testid={`whatsapp-aviso-${registro.id}`}>
                                <i className="fab fa-whatsapp text-green-600 text-2xl mt-0.5"></i>
                                <div className="flex-1">
                                  <p className="font-semibold text-green-800 text-sm">Recibo enviado por WhatsApp</p>
                                  <p className="text-xs text-green-700 mt-0.5">
                                    Revisá tu WhatsApp (+1 646 295 2037). Si recibiste el recibo, confirmá aquí.
                                  </p>
                                  <Button
                                    onClick={async () => {
                                      if (!window.confirm(`¿Confirmar que recibiste el recibo de ${registro.estudiante_nombre} por WhatsApp?\n\nEl estudiante será inscrito automáticamente en las clases del semestre.`)) return;
                                      const btnElement = document.activeElement;
                                      if (btnElement) btnElement.disabled = true;
                                      try {
                                        const resp = await axios.post(
                                          `${API}/admin/registraciones/${registro.id}/confirmar-whatsapp`,
                                          {},
                                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 30000 }
                                        );
                                        if (resp?.data?.success) {
                                          toast.success(resp.data.message || '✅ Recibo confirmado. Estudiante inscrito y notificado.');
                                          await loadData();
                                        } else {
                                          toast.error('Respuesta inesperada del servidor. Refrescá la página.');
                                        }
                                      } catch (err) {
                                        const msg = err?.response?.data?.detail || err?.message || 'Error al confirmar';
                                        toast.error(`❌ ${msg}`);
                                      } finally {
                                        if (btnElement) btnElement.disabled = false;
                                      }
                                    }}
                                    className="mt-2 bg-green-600 hover:bg-green-700 text-white"
                                    size="sm"
                                    data-testid={`btn-confirmar-whatsapp-${registro.id}`}
                                  >
                                    <i className="fas fa-check mr-1"></i>
                                    Confirmar recibo recibido
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="flex space-x-2 ml-4">
                            <Button
                              onClick={() => openReviewDialog(registro)}
                              className="instituto-gradient text-white btn-hover"
                              size="sm"
                            >
                              <i className="fas fa-gavel mr-1"></i>
                              Revisar
                            </Button>
                            <Button
                              onClick={() => openInvoiceDialog(registro)}
                              variant="outline"
                              size="sm"
                            >
                              <i className="fas fa-file-invoice mr-1"></i>
                              Facturar
                            </Button>
                            <Button
                              onClick={() => openDeleteDialog(registro)}
                              variant="destructive"
                              size="sm"
                            >
                              <i className="fas fa-trash mr-1"></i>
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Todas las Registraciones */}
        <TabsContent value="todas">
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center">
                <i className="fas fa-list mr-3 text-blue-600"></i>
                Todas las Registraciones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {todasRegistraciones.map((registro) => (
                  <Card
                    key={registro.id}
                    data-registro-card={registro.id}
                    className={`border-l-4 border-l-blue-500 transition-all duration-500 ${
                      highlightedRegistroId === registro.id
                        ? 'ring-4 ring-blue-400 shadow-2xl scale-[1.01] bg-blue-50'
                        : ''
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-semibold text-lg">{registro.estudiante_nombre}</h3>
                            {registro.es_renovacion && (
                              <Badge className="bg-purple-500 text-white">
                                <i className="fas fa-redo mr-1"></i>
                                Renovación
                              </Badge>
                            )}
                            {!registro.es_renovacion && (
                              <Badge className="bg-green-500 text-white">
                                <i className="fas fa-star mr-1"></i>
                                Nuevo
                              </Badge>
                            )}
                            {getStatusBadge(registro.estado_pago)}
                            {registro.loyverse_receipt_number && (
                              <Badge className="bg-emerald-600 text-white" data-testid={`loyverse-badge-${registro.id}`}>
                                <i className="fas fa-receipt mr-1"></i>
                                Loyverse #{registro.loyverse_receipt_number}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Semestre:</span>
                              <p className="font-medium">{registro.semestre_nombre}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Monto:</span>
                              <p className="font-medium">{formatPrice(registro.monto, registro.moneda)}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Método:</span>
                              <p className="font-medium">{getPaymentMethodName(registro.metodo_pago)}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Fecha:</span>
                              <p className="font-medium">{formatDate(registro.fecha_registro)}</p>
                            </div>
                            {registro.fecha_aprobacion && (
                              <div>
                                <span className="text-gray-600">Aprobado:</span>
                                <p className="font-medium">{formatDate(registro.fecha_aprobacion)}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 ml-4">
                          {/* Botón para ver comprobante original del estudiante */}
                          {(registro.comprobante_url || registro.comprobante_pago_url) && (
                            <Button
                              onClick={() => {
                                const url = registro.comprobante_url || registro.comprobante_pago_url;
                                setComprobanteModal({ 
                                  open: true, 
                                  url: url,
                                  registroId: registro.id,
                                  estudianteNombre: registro.estudiante_nombre
                                });
                              }}
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            >
                              <i className="fas fa-image mr-1"></i>
                              Comprobante
                            </Button>
                          )}
                          {registro.estado_pago === 'aprobado' && (
                            <Button
                              onClick={() => downloadReceipt(registro.id)}
                              variant="outline"
                              size="sm"
                            >
                              <i className="fas fa-download mr-1"></i>
                              Recibo PDF
                            </Button>
                          )}
                          {registro.loyverse_receipt_number && (
                            <Button
                              onClick={() => openLoyverseReceipt(registro.loyverse_receipt_number)}
                              variant="outline"
                              size="sm"
                              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                              data-testid={`btn-imprimir-loyverse-${registro.id}`}
                              title="Abre Loyverse POS para reimprimir desde la impresora térmica"
                            >
                              <i className="fas fa-print mr-1"></i>
                              Imprimir en Loyverse
                            </Button>
                          )}
                          <Button
                            onClick={() => openDeleteDialog(registro)}
                            variant="destructive"
                            size="sm"
                          >
                            <i className="fas fa-trash mr-1"></i>
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Facturación */}
        <TabsContent value="facturacion">
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center">
                <i className="fas fa-file-invoice-dollar mr-3 text-green-600"></i>
                Sistema de Facturación
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <i className="fas fa-tools text-gray-400 text-5xl mb-4"></i>
                <p className="text-gray-600">Panel de facturación en desarrollo</p>
                <p className="text-sm text-gray-500">Próximamente: Generación automática de facturas y reportes</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={isReviewDialogOpen} onOpenChange={setIsReviewDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Revisar Registración</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {revisingRegistration && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Información de la Registración</h4>
                <p><strong>Estudiante:</strong> {revisingRegistration.estudiante_nombre}</p>
                <p><strong>Semestre:</strong> {revisingRegistration.semestre_nombre}</p>
                <p><strong>Monto:</strong> {formatPrice(revisingRegistration.monto, revisingRegistration.moneda)}</p>
              </div>
            )}
            
            <div>
              <Label htmlFor="review-action">Acción</Label>
              <Select value={reviewForm.accion} onValueChange={(value) => setReviewForm({ ...reviewForm, accion: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una acción" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aprobar">✅ Aprobar Registración</SelectItem>
                  <SelectItem value="rechazar">❌ Rechazar Registración</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="review-comments">Comentarios</Label>
              <Textarea
                id="review-comments"
                value={reviewForm.comentarios}
                onChange={(e) => setReviewForm({ ...reviewForm, comentarios: e.target.value })}
                placeholder="Agregar comentarios sobre la decisión..."
                rows={3}
              />
            </div>

            {reviewForm.accion === 'aprobar' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                {revisingRegistration?.loyverse_receipt_number ? (
                  <div className="flex items-start gap-2 text-sm text-emerald-800">
                    <span>🧾</span>
                    <div className="flex-1">
                      <p className="font-semibold">
                        Ya existe un recibo Loyverse: #{revisingRegistration.loyverse_receipt_number}
                      </p>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        No se emitirá uno nuevo (idempotencia).
                      </p>
                      <Button
                        onClick={() => openLoyverseReceipt(revisingRegistration.loyverse_receipt_number)}
                        variant="outline"
                        size="sm"
                        className="mt-2 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                        data-testid="btn-imprimir-loyverse-modal"
                      >
                        <i className="fas fa-print mr-1"></i>
                        Imprimir en Loyverse
                      </Button>
                    </div>
                  </div>
                ) : (
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!reviewForm.emitirReciboLoyverse}
                      onChange={(e) =>
                        setReviewForm({ ...reviewForm, emitirReciboLoyverse: e.target.checked })
                      }
                      className="mt-0.5 w-4 h-4 accent-emerald-600 cursor-pointer"
                      data-testid="checkbox-loyverse-receipt"
                    />
                    <div className="text-sm">
                      <p className="font-semibold text-emerald-800 flex items-center gap-1">
                        🧾 Emitir recibo oficial en Loyverse
                      </p>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        Se crea el recibo en tu cuenta Loyverse y Loyverse envía el PDF por
                        email al estudiante automáticamente. Se omite si el monto es 0 o el
                        método es "becado".
                      </p>
                    </div>
                  </label>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsReviewDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleReview}
                disabled={!reviewForm.accion}
                className="instituto-gradient text-white"
              >
                Procesar Revisión
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={isInvoiceDialogOpen} onOpenChange={setIsInvoiceDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Enviar Factura</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedForInvoice && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Generar Factura Para</h4>
                <p><strong>Estudiante:</strong> {selectedForInvoice.estudiante_nombre}</p>
                <p><strong>Email:</strong> {selectedForInvoice.estudiante_email}</p>
                <p><strong>Semestre:</strong> {selectedForInvoice.semestre_nombre}</p>
                <p><strong>Monto:</strong> {formatPrice(selectedForInvoice.monto, selectedForInvoice.moneda)}</p>
              </div>
            )}
            
            <div>
              <Label htmlFor="invoice-due-date">Fecha de Vencimiento</Label>
              <Input
                id="invoice-due-date"
                type="date"
                value={invoiceForm.fecha_vencimiento}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, fecha_vencimiento: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="invoice-discount">Descuento (%)</Label>
              <Input
                id="invoice-discount"
                type="number"
                min="0"
                max="100"
                value={invoiceForm.descuento_porcentaje}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, descuento_porcentaje: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div>
              <Label htmlFor="invoice-notes">Nota Adicional</Label>
              <Textarea
                id="invoice-notes"
                value={invoiceForm.nota_adicional}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, nota_adicional: e.target.value })}
                placeholder="Nota adicional para incluir en la factura..."
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsInvoiceDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSendInvoice}
                className="instituto-gradient text-white"
              >
                <i className="fas fa-paper-plane mr-2"></i>
                Enviar Factura
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo de Configuración de Link de Registro */}
      <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center">
                <i className="fas fa-link mr-2 text-blue-600"></i>
                Configurar Link de Registro
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Formulario */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="tipo_formulario">Tipo de Formulario</Label>
                  <Select
                    value={configForm.tipo_formulario}
                    onValueChange={(value) => setConfigForm({...configForm, tipo_formulario: value})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wix">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-globe text-blue-600"></i>
                          <span>Wix Forms</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="google_classroom">
                        <div className="flex items-center gap-2">
                          <i className="fab fa-google text-red-600"></i>
                          <span>Google Classroom</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {configForm.tipo_formulario === 'wix' ? 'Formulario de Wix' : 'Formulario de Google Classroom'}
                  </p>
                </div>

                <div>
                  <Label htmlFor="link_registro">Link del Formulario *</Label>
                  <Input
                    id="link_registro"
                    type="url"
                    placeholder={configForm.tipo_formulario === 'wix' ? 'https://ejemplo.wixsite.com/formulario' : 'https://classroom.google.com/...'}
                    value={configForm.link_registro}
                    onChange={(e) => setConfigForm({...configForm, link_registro: e.target.value})}
                    className="mt-1"
                  />
                  {configForm.link_registro && !validateFormLink(configForm.link_registro) && (
                    <p className="text-sm text-amber-600 mt-1">
                      ⚠️ Por favor ingrese una URL válida
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="titulo">Título</Label>
                  <Input
                    id="titulo"
                    value={configForm.titulo}
                    onChange={(e) => setConfigForm({...configForm, titulo: e.target.value})}
                    className="mt-1"
                    placeholder="Título que verán los estudiantes"
                  />
                </div>

                <div>
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Textarea
                    id="descripcion"
                    value={configForm.descripcion}
                    onChange={(e) => setConfigForm({...configForm, descripcion: e.target.value})}
                    className="mt-1"
                    rows={3}
                    placeholder="Instrucciones para los estudiantes"
                  />
                </div>
              </div>

              {/* Vista Previa */}
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 bg-gray-50">
                <h4 className="font-semibold text-sm text-gray-700 mb-3">Vista Previa:</h4>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-center space-y-3">
                    <div className="text-2xl">📋</div>
                    <h3 className="font-semibold text-gray-800">
                      {configForm.titulo || 'Título del formulario'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {configForm.descripcion || 'Descripción del formulario'}
                    </p>
                    <Button 
                      size="sm" 
                      disabled
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <i className="fas fa-external-link-alt mr-2"></i>
                      Completar Formulario
                    </Button>
                  </div>
                </div>
                {!configForm.link_registro && (
                  <p className="text-xs text-gray-500 italic mt-2 text-center">
                    Configura el link para activar el botón
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsConfigDialogOpen(false)}
              >
                Cancelar
              </Button>
              {configForm.link_registro && (
                <Button
                  variant="outline"
                  onClick={() => window.open(configForm.link_registro, '_blank')}
                >
                  <i className="fas fa-external-link-alt mr-2"></i>
                  Ver Formulario
                </Button>
              )}
              <Button
                onClick={handleSaveConfig}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <i className="fas fa-save mr-2"></i>
                {registroConfig ? 'Actualizar' : 'Guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>¿Estás seguro de que deseas eliminar este registro de pago?</p>
            {deletingRegistration && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>Estudiante:</strong> {deletingRegistration.estudiante_nombre}</p>
                <p><strong>Semestre:</strong> {deletingRegistration.semestre_nombre}</p>
                <p><strong>Monto:</strong> {formatPrice(deletingRegistration.monto, deletingRegistration.moneda)}</p>
              </div>
            )}
            <p className="text-red-600 text-sm">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
              >
                <i className="fas fa-trash mr-2"></i>
                Eliminar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para ver comprobante */}
      <Dialog open={comprobanteModal.open} onOpenChange={(open) => setComprobanteModal({ ...comprobanteModal, open })}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-file-image text-blue-600"></i>
              Comprobante de Pago
              {comprobanteModal.estudianteNombre && (
                <span className="text-sm font-normal text-gray-500">
                  - {comprobanteModal.estudianteNombre}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg min-h-[300px]">
            {comprobanteModal.url && (
              comprobanteModal.url.includes('application/pdf') ? (
                <div className="text-center">
                  <i className="fas fa-file-pdf text-red-500 text-6xl mb-4"></i>
                  <p className="text-gray-600 mb-4">Este comprobante es un PDF</p>
                  <Button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = comprobanteModal.url;
                      link.download = 'comprobante.pdf';
                      link.click();
                    }}
                  >
                    <i className="fas fa-download mr-2"></i>
                    Descargar PDF
                  </Button>
                </div>
              ) : (
                <img 
                  src={comprobanteModal.url} 
                  alt="Comprobante de pago" 
                  className="max-w-full max-h-[70vh] object-contain rounded shadow-lg"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '';
                    e.target.alt = 'Error al cargar imagen';
                  }}
                />
              )
            )}
          </div>
          <div className="flex justify-between mt-4">
            {/* Botón MEGA a la izquierda */}
            <Button
              variant="outline"
              onClick={handleUploadToMega}
              disabled={uploadingToMega}
              className="bg-red-50 hover:bg-red-100 text-red-700 border-red-300"
            >
              {uploadingToMega ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Subiendo...
                </>
              ) : (
                <>
                  <i className="fas fa-cloud-upload-alt mr-2"></i>
                  Enviar a MEGA
                </>
              )}
            </Button>
            
            {/* Botones a la derecha */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  if (comprobanteModal.url) {
                    const link = document.createElement('a');
                    link.href = comprobanteModal.url;
                    link.download = 'comprobante';
                    link.click();
                  }
                }}
              >
                <i className="fas fa-download mr-2"></i>
                Descargar
              </Button>
              <Button onClick={() => setComprobanteModal({ open: false, url: null, registroId: null, estudianteNombre: null })}>
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRegistraciones;